import { getApiError } from '../../utils/errorHelper';
import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, Play, Calendar, Clock, CheckCircle, 
  AlertCircle, Loader2, FileText, History, AlertTriangle, Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import { toIST } from '../../utils/dateUtils';

const RecurringTab = () => {
  const [latestRun, setLatestRun] = useState(null);
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [triggeringYearly, setTriggeringYearly] = useState(false);
  const [triggeringCustom, setTriggeringCustom] = useState(false);
  const [triggeringMonthly, setTriggeringMonthly] = useState(false);
  const [triggeringQuarterly, setTriggeringQuarterly] = useState(false);
  const [triggeringReminders, setTriggeringReminders] = useState(false);
  const [yearInput, setYearInput] = useState(new Date().getFullYear());

  useEffect(() => { loadData(); }, [logsPage]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [latest, logsData] = await Promise.all([
        adminApi.getLatestSchedulerRun(),
        adminApi.getSchedulerLogs(logsPage, 15),
      ]);
      setLatestRun(latest);
      setLogs(logsData.logs || []);
      setTotalLogs(logsData.total || 0);
    } catch (error) {
      console.error('Failed to load scheduler data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerYearly = async () => {
    setTriggeringYearly(true);
    try {
      const res = await adminApi.triggerYearlyOrders(yearInput);
      toast.success(`Yearly trigger complete: ${res.orders_generated} orders generated`);
      loadData();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to trigger yearly orders'));
    } finally {
      setTriggeringYearly(false);
    }
  };

  const handleTriggerCustom = async () => {
    setTriggeringCustom(true);
    try {
      const res = await adminApi.triggerCustomOrders();
      toast.success(`Custom trigger complete: ${res.orders_generated} orders generated`);
      loadData();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to trigger custom orders'));
    } finally {
      setTriggeringCustom(false);
    }
  };

  const handleTriggerReminders = async () => {
    setTriggeringReminders(true);
    try {
      const res = await adminApi.triggerReminders();
      toast.success(`Reminders sent: ${res.reminders_sent}`);
      loadData();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to trigger reminders'));
    } finally {
      setTriggeringReminders(false);
    }
  };

  const handleTriggerMonthly = async () => {
    setTriggeringMonthly(true);
    try {
      const res = await adminApi.triggerMonthlyOrders();
      toast.success(`Monthly trigger complete: ${res.orders_generated} orders generated`);
      loadData();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to trigger monthly orders'));
    } finally {
      setTriggeringMonthly(false);
    }
  };

  const handleTriggerQuarterly = async () => {
    setTriggeringQuarterly(true);
    try {
      const res = await adminApi.triggerQuarterlyOrders();
      toast.success(`Quarterly trigger complete: ${res.orders_generated} orders generated`);
      loadData();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to trigger quarterly orders'));
    } finally {
      setTriggeringQuarterly(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="scheduler-tab">
      {/* Latest Run Status */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Clock className="w-5 h-5 text-indigo-400" />
          <h3 className="text-white font-semibold">Last Scheduler Run</h3>
        </div>
        {latestRun?.run_at ? (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div>
              <p className="text-xs text-slate-500 mb-1">Run Time</p>
              <p className="text-sm text-white">{toIST(latestRun.run_at)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Yearly Orders</p>
              <p className="text-sm text-white">{latestRun.yearly_orders_generated || 0}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Custom Orders</p>
              <p className="text-sm text-white">{latestRun.custom_orders_generated || 0}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Monthly Orders</p>
              <p className="text-sm text-white">{latestRun.monthly_orders_generated || 0}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Quarterly Orders</p>
              <p className="text-sm text-white">{latestRun.quarterly_orders_generated || 0}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Reminders Sent</p>
              <p className="text-sm text-white">{latestRun.reminders_sent || 0}</p>
            </div>
            {latestRun.status && (
              <div>
                <p className="text-xs text-slate-500 mb-1">Status</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  latestRun.status === 'success' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                }`}>
                  {latestRun.status}
                </span>
              </div>
            )}
            {latestRun.manual_trigger && (
              <div>
                <p className="text-xs text-slate-500 mb-1">Type</p>
                <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400 font-medium">Manual</span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-slate-500 text-sm">No scheduler runs recorded yet.</p>
        )}
      </div>

      {/* Manual Triggers */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Zap className="w-5 h-5 text-amber-400" />
          <h3 className="text-white font-semibold">Manual Triggers</h3>
          <span className="text-xs text-slate-500 bg-slate-700/50 px-2 py-0.5 rounded">Super Admin Only</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {/* Yearly Trigger */}
          <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-indigo-400" />
              <h4 className="text-sm font-medium text-white">Yearly Orders</h4>
            </div>
            <p className="text-xs text-slate-500 mb-3">Generate yearly auto-renewal orders for a specific financial year.</p>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={yearInput}
                onChange={(e) => setYearInput(parseInt(e.target.value) || new Date().getFullYear())}
                className="w-24 h-8 bg-slate-800 border-slate-600 text-white text-sm"
                data-testid="yearly-year-input"
              />
              <Button
                onClick={handleTriggerYearly}
                disabled={triggeringYearly}
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
                data-testid="trigger-yearly-btn"
              >
                {triggeringYearly ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
                Run
              </Button>
            </div>
          </div>

          {/* Monthly Trigger */}
          <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-emerald-400" />
              <h4 className="text-sm font-medium text-white">Monthly Orders</h4>
            </div>
            <p className="text-xs text-slate-500 mb-3">Generate monthly filing orders (GST, TDS challan) for the previous month.</p>
            <Button
              onClick={handleTriggerMonthly}
              disabled={triggeringMonthly}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              data-testid="trigger-monthly-btn"
            >
              {triggeringMonthly ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
              Run Monthly
            </Button>
          </div>

          {/* Quarterly Trigger */}
          <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-purple-400" />
              <h4 className="text-sm font-medium text-white">Quarterly Orders</h4>
            </div>
            <p className="text-xs text-slate-500 mb-3">Generate quarterly filing orders (GST QRMP, TDS returns) for the previous quarter.</p>
            <Button
              onClick={handleTriggerQuarterly}
              disabled={triggeringQuarterly}
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
              data-testid="trigger-quarterly-btn"
            >
              {triggeringQuarterly ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
              Run Quarterly
            </Button>
          </div>

          {/* Custom Trigger */}
          <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <RefreshCw className="w-4 h-4 text-cyan-400" />
              <h4 className="text-sm font-medium text-white">Custom Renewals</h4>
            </div>
            <p className="text-xs text-slate-500 mb-3">Check and generate custom auto-renewal orders due within 45 days.</p>
            <Button
              onClick={handleTriggerCustom}
              disabled={triggeringCustom}
              size="sm"
              className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs"
              data-testid="trigger-custom-btn"
            >
              {triggeringCustom ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
              Run Custom Check
            </Button>
          </div>

          {/* Reminder Trigger */}
          <div className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <h4 className="text-sm font-medium text-white">Send Reminders</h4>
            </div>
            <p className="text-xs text-slate-500 mb-3">Dispatch daily reminder emails for orders within the 45-day countdown.</p>
            <Button
              onClick={handleTriggerReminders}
              disabled={triggeringReminders}
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
              data-testid="trigger-reminders-btn"
            >
              {triggeringReminders ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
              Send Reminders
            </Button>
          </div>
        </div>
      </div>

      {/* Scheduler Logs */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <History className="w-5 h-5 text-slate-400" />
            <h3 className="text-white font-semibold">Run History</h3>
            <span className="text-xs text-slate-500">{totalLogs} runs</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadData}
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {logs.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-8">No scheduler logs yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="scheduler-logs-table">
              <thead>
                <tr className="text-left border-b border-slate-700/50">
                  <th className="px-3 py-2 text-xs text-slate-400 font-medium">Date</th>
                  <th className="px-3 py-2 text-xs text-slate-400 font-medium">Status</th>
                  <th className="px-3 py-2 text-xs text-slate-400 font-medium">Yearly</th>
                  <th className="px-3 py-2 text-xs text-slate-400 font-medium">Custom</th>
                  <th className="px-3 py-2 text-xs text-slate-400 font-medium">Reminders</th>
                  <th className="px-3 py-2 text-xs text-slate-400 font-medium">Type</th>
                  <th className="px-3 py-2 text-xs text-slate-400 font-medium">By</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => (
                  <tr key={log.id || idx} className="border-b border-slate-700/20 hover:bg-slate-800/30">
                    <td className="px-3 py-2.5 text-xs text-white">{toIST(log.run_at)}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        log.status === 'success' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {log.status || '-'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-300">{log.yearly_orders_generated || 0}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-300">{log.custom_orders_generated || 0}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-300">{log.reminders_sent || 0}</td>
                    <td className="px-3 py-2.5">
                      {log.manual_trigger ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-indigo-500/15 text-indigo-400 border border-indigo-500/20">Manual</span>
                      ) : (
                        <span className="text-xs text-slate-500">Auto</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-400">{log.triggered_by || 'System'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalLogs > 15 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLogsPage(p => Math.max(1, p - 1))}
              disabled={logsPage <= 1}
              className="text-slate-400 text-xs"
            >
              Previous
            </Button>
            <span className="text-xs text-slate-500">Page {logsPage} of {Math.ceil(totalLogs / 15)}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLogsPage(p => p + 1)}
              disabled={logsPage >= Math.ceil(totalLogs / 15)}
              className="text-slate-400 text-xs"
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="bg-slate-800/30 border border-slate-700/30 rounded-lg p-4">
        <p className="text-xs text-slate-500">
          <strong className="text-slate-400">Auto Schedule:</strong> The scheduler runs daily at 6:00 AM IST (12:30 AM UTC). 
          It generates yearly orders on April 1st, custom orders 45 days before renewal, and sends daily reminder emails for active orders within the collection window.
        </p>
      </div>
    </div>
  );
};

export default RecurringTab;
